import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Form, Typography, Divider, Spin, Input, Checkbox, Space, Alert } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { LocationDTO, LocationConfigDTO } from '@/shared/api/model';
import LocationCascade from './LocationCascade';
import useLocation from '../hooks/useLocation';
import { useLocationConfig } from '../hooks/useLocationConfig';
import GPSCoordinates from './GPSCoordinates';

const { Text, Title } = Typography;

export interface ConfigurableLocationSelectorProps {
  title?: string;
  entityType: string;
  addressType: string;
  onLocationSelected?: (locationData: {
    locationSelections: {
      zone?: LocationDTO;
      region?: LocationDTO;
      district?: LocationDTO;
      ward?: LocationDTO;
      street?: LocationDTO;
      fullPath?: string;
    },
    gpsCoordinates?: {
      latitude?: number;
      longitude?: number;
      accuracy?: number;
    },
    houseNumber?: string;
    postalCode?: string;
    isPrimary?: boolean;
    validationErrors?: string[];
  }) => void;
  disabled?: boolean;
  initialValues?: {
    locationSelections?: {
      zone?: LocationDTO;
      region?: LocationDTO;
      district?: LocationDTO;
      ward?: LocationDTO;
      street?: LocationDTO;
    },
    gpsCoordinates?: {
      latitude?: number;
      longitude?: number;
      accuracy?: number;
    },
    zoneId?: number;
    regionId?: number;
    districtId?: number;
    wardId?: number;
    streetId?: number;
    houseNumber?: string;
    postalCode?: string;
    isPrimary?: boolean;
  };
}

export const ConfigurableLocationSelector: React.FC<ConfigurableLocationSelectorProps> = ({
                                                                                            title,
                                                                                            entityType,
                                                                                            addressType,
                                                                                            onLocationSelected,
                                                                                            disabled = false,
                                                                                            initialValues
                                                                                          }) => {
  const [fullPath, setFullPath] = useState<string>('');
  const [config, setConfig] = useState<LocationConfigDTO | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [formData, setFormData] = useState({
    locationSelections: initialValues?.locationSelections || {},
    gpsCoordinates: initialValues?.gpsCoordinates || {},
    houseNumber: initialValues?.houseNumber || '',
    postalCode: initialValues?.postalCode || '',
    isPrimary: initialValues?.isPrimary || false
  });
  const [form] = Form.useForm();

  const { locationHierarchy } = useLocation();
  const { fetchConfigsByEntityType, configs } = useLocationConfig();

  // Load configurations for the entity type
  useEffect(() => {
    fetchConfigsByEntityType(entityType, false);
  }, [entityType, fetchConfigsByEntityType]);

  // Find the configuration that matches our entity and address type
  useEffect(() => {
    setIsLoading(true);
    if (configs) {
      console.log('ConfigurableLocationSelector - Looking for config:', { 
        entityType, 
        addressType, 
        availableConfigs: configs.map(c => ({ 
          name: c.configName, 
          entity: c.entityType, 
          address: c.addressType,
          active: c.isActive 
        }))
      });
      
      const matchedConfig = configs.find(c =>
          c.entityType === entityType &&
          c.addressType === addressType &&
          c.isActive === true
      );

      if (matchedConfig) {
        console.log('ConfigurableLocationSelector - Config found:', matchedConfig);
        setConfig(matchedConfig);
      } else {
        console.warn('ConfigurableLocationSelector - No config found for:', { entityType, addressType });
        // Still set loading to false even if no config found
        // Will use default levels
      }
      setIsLoading(false);
    }
  }, [configs, entityType, addressType]);

  // Determine which location levels are required based on the configuration
  const mandatoryLevels = useMemo(() => {
    const levels = {
      zone: false,
      region: false,
      district: false,
      ward: false,
      street: false
    };

    if (config?.configJson?.requiredLevels) {
      for (const level of config.configJson.requiredLevels) {
        if (level.level === 'ZONE') {
          levels.zone = level.mandatory === true;
        } else if (level.level === 'REGION') {
          levels.region = level.mandatory === true;
        } else if (level.level === 'DISTRICT') {
          levels.district = level.mandatory === true;
        } else if (level.level === 'WARD') {
          levels.ward = level.mandatory === true;
        } else if (level.level === 'STREET') {
          levels.street = level.mandatory === true;
        }
      }
    }

    return levels;
  }, [config]);

  // Determine which location levels are visible based on the configuration
  const visibleLevels = useMemo(() => {
    const levels = {
      zone: false,
      region: false,
      district: false,
      ward: false,
      street: false
    };

    // If config exists, show levels that are defined in requiredLevels
    if (config?.configJson?.requiredLevels) {
      for (const level of config.configJson.requiredLevels) {
        if (level.level === 'ZONE') {
          levels.zone = true;
        } else if (level.level === 'REGION') {
          levels.region = true;
        } else if (level.level === 'DISTRICT') {
          levels.district = true;
        } else if (level.level === 'WARD') {
          levels.ward = true;
        } else if (level.level === 'STREET') {
          levels.street = true;
        }
      }
    } else {
      // If no config, show default levels (zone, region, district, ward)
      levels.zone = true;
      levels.region = true;
      levels.district = true;
      levels.ward = true;
    }

    return levels;
  }, [config]);

  // Determine if house number is required
  const houseNumberRequired = useMemo(() => {
    return config?.configJson?.validation?.requireHouseNumber === true;
  }, [config]);

  // Determine if postal code is required
  const postalCodeRequired = useMemo(() => {
    return config?.configJson?.validation?.requirePostalCode === true;
  }, [config]);

  // Determine if GPS coordinates are required
  const gpsRequired = useMemo(() => {
    return config?.configJson?.gpsCoordinates?.required === true;
  }, [config]);

  // Determine the GPS accuracy threshold
  const gpsAccuracyThreshold = useMemo(() => {
    return config?.configJson?.gpsCoordinates?.accuracyThreshold || 100;
  }, [config]);

  // Helper function to validate and emit errors
  const validateAndEmitErrors = useCallback((currentData: any) => {
    // Validate using the current data
    const errors = [];

    console.log('Validating with current data:', JSON.stringify(currentData, null, 2));
    console.log('Mandatory levels:', mandatoryLevels);
    console.log('Visible levels:', visibleLevels);

    // Check for mandatory location levels
    Object.entries(mandatoryLevels).forEach(([level, isMandatory]) => {
      if (isMandatory && visibleLevels[level as keyof typeof visibleLevels]) {
        // Only validate if the level is both mandatory AND visible
        const selection = currentData.locationSelections?.[level as keyof typeof currentData.locationSelections];

        console.log(`Validating ${level}:`, JSON.stringify(selection, null, 2));

        // Check if selection exists and has an id property with a valid value
        // Note: id=0 is considered valid in this system
        const hasValue = selection &&
            typeof selection === 'object' &&
            selection !== null &&
            'id' in selection &&
            selection.id !== null &&
            selection.id !== undefined;

        console.log(`${level} hasValue:`, hasValue, selection?.id);

        if (!hasValue) {
          errors.push(`${level.charAt(0).toUpperCase() + level.slice(1)} is required`);
        }
      }
    });

    // Check for house number
    if (houseNumberRequired && !currentData.houseNumber?.trim()) {
      errors.push('House number is required');
    }

    // Check for postal code
    if (postalCodeRequired && !currentData.postalCode?.trim()) {
      errors.push('Postal code is required');
    }

    // Check for GPS coordinates
    if (gpsRequired && (!currentData.gpsCoordinates?.latitude || !currentData.gpsCoordinates?.longitude)) {
      errors.push('GPS coordinates are required');
    }

    console.log('Final validation errors:', errors);

    // Send the data and validation errors to the parent component
    if (onLocationSelected) {
      onLocationSelected({
        ...currentData,
        validationErrors: errors
      });
    }

    return errors;
  }, [mandatoryLevels, visibleLevels, houseNumberRequired, postalCodeRequired, gpsRequired, onLocationSelected]);

  // Handle location selection changes
  const handleLocationChange = useCallback((selections: any) => {
    console.log('DEBUG - handleLocationChange');
    console.log('Selections received:', selections);

    // First update the form data state
    setFormData(prev => {
      const updatedData = {
        ...prev,
        locationSelections: selections
      };

      // Validate and emit errors after state update
      setTimeout(() => {
        validateAndEmitErrors(updatedData);
      }, 0);

      return updatedData;
    });
  }, [validateAndEmitErrors]);

  // Handle GPS coordinates changes
  const handleGPSChange = useCallback((coordinates: any) => {
    // First update the form data state
    setFormData(prev => {
      const updatedData = {
        ...prev,
        gpsCoordinates: coordinates
      };

      // Validate and emit errors after state update
      setTimeout(() => {
        validateAndEmitErrors(updatedData);
      }, 0);

      return updatedData;
    });
  }, [validateAndEmitErrors]);

  // Handle input changes (house number, postal code)
  const handleInputChange = useCallback((field: string, value: string | boolean) => {
    // First update the form data state
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [field]: value
      };

      // Validate and emit errors after state update
      setTimeout(() => {
        validateAndEmitErrors(updatedData);
      }, 0);

      return updatedData;
    });
  }, [validateAndEmitErrors]);

  // Initialize form data with initial values if provided
  useEffect(() => {
    console.log('ConfigurableLocationSelector - initialValues received:', initialValues);

    if (initialValues) {
      setFormData(prev => {
        const newData = {
          ...prev,
          ...initialValues
        };
        console.log('ConfigurableLocationSelector - formData after initialValues update:', newData);
        return newData;
      });
    }
  }, [initialValues]);

  // Run initial validation when config and mandatory levels are loaded
  useEffect(() => {
    if (config && Object.keys(mandatoryLevels).length > 0) {
      console.log('Running initial validation check with config loaded');

      // Small delay to ensure all data is properly loaded
      setTimeout(() => {
        validateAndEmitErrors(formData);
      }, 100);
    }
  }, [config, mandatoryLevels, validateAndEmitErrors]);

  if (isLoading) {
    return (
        <Card className="mb-4">
          <div className="flex justify-center items-center p-4">
            <Spin />
          </div>
        </Card>
    );
  }

  return (
      <Card
          className="mb-4"
          title={
            <div className="flex items-center">
              <EnvironmentOutlined className="mr-2" />
              <span>{title || `${addressType} Location`}</span>
            </div>
          }
      >
        <div className="space-y-4">
          {!config && (
            <Alert
              message="Using Default Configuration"
              description={`No location configuration found for ${entityType} - ${addressType}. Using default location levels (Zone, Region, District, Ward).`}
              type="warning"
              showIcon
              className="mb-4"
            />
          )}
          
          <Form form={form} layout="vertical">
            <LocationCascade
                disabled={disabled}
                required={mandatoryLevels}
                // Show each level based on whether it's visible according to hierarchy rules
                zone={visibleLevels.zone}
                region={visibleLevels.region}
                district={visibleLevels.district}
                ward={visibleLevels.ward}
                street={visibleLevels.street}
                onSelectionChange={handleLocationChange}
                initialValues={{
                  zoneId: initialValues?.zoneId,
                  regionId: initialValues?.regionId,
                  districtId: initialValues?.districtId,
                  wardId: initialValues?.wardId,
                  streetId: initialValues?.streetId,
                }}
            />

            {fullPath && (
                <>
                  <Divider className="my-2" />
                  <div className="text-sm">
                    <Text type="secondary">Full Path:</Text>
                    <div className="mt-1 text-gray-700 dark:text-gray-300">
                      {fullPath}
                    </div>
                  </div>
                </>
            )}
          </Form>

          {/* Additional fields based on configuration */}
          <Space direction="vertical" className="w-full">
            {/* House number field */}
            {houseNumberRequired && (
                <Form.Item
                    label="House Number / Plot Number"
                    required={houseNumberRequired}
                    validateStatus={formData.houseNumber || !houseNumberRequired ? undefined : 'error'}
                    help={!formData.houseNumber && houseNumberRequired ? 'House number is required' : undefined}
                >
                  <Input
                      placeholder="Enter house/plot number"
                      value={formData.houseNumber}
                      onChange={(e) => handleInputChange('houseNumber', e.target.value)}
                      disabled={disabled}
                  />
                </Form.Item>
            )}

            {/* Postal code field */}
            {postalCodeRequired && (
                <Form.Item
                    label="Postal Code"
                    required={postalCodeRequired}
                    validateStatus={formData.postalCode || !postalCodeRequired ? undefined : 'error'}
                    help={!formData.postalCode && postalCodeRequired ? 'Postal code is required' : undefined}
                >
                  <Input
                      placeholder="Enter postal code"
                      value={formData.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      disabled={disabled}
                  />
                </Form.Item>
            )}

            {/* GPS coordinates field */}
            {gpsRequired && (
                <Form.Item
                    label="GPS Coordinates"
                    required={gpsRequired}
                    validateStatus={
                      (formData.gpsCoordinates?.latitude && formData.gpsCoordinates?.longitude) || !gpsRequired
                          ? undefined
                          : 'error'
                    }
                    help={
                      (!formData.gpsCoordinates?.latitude || !formData.gpsCoordinates?.longitude) && gpsRequired
                          ? 'GPS coordinates are required'
                          : undefined
                    }
                >
                  <GPSCoordinates
                      // @ts-ignore
                      onChange={handleGPSChange}
                      required={gpsRequired}
                      accuracyThreshold={gpsAccuracyThreshold}
                      initialCoordinates={formData.gpsCoordinates}
                      disabled={disabled}
                  />
                </Form.Item>
            )}

            {/* Primary address checkbox */}
            <Form.Item>
              <Checkbox
                  checked={formData.isPrimary}
                  onChange={(e) => handleInputChange('isPrimary', e.target.checked)}
                  disabled={disabled}
              >
                Set as primary address
              </Checkbox>
            </Form.Item>
          </Space>
        </div>
      </Card>
  );
};

export default ConfigurableLocationSelector;